import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./Popup.module.scss";
import { XClose } from "@untitled-ui/icons-react";
import useViewportSize from "@/hooks/useViewportSize";
import PopupCornerSparkles from "./PopupCornerSparkles";

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string | React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  headerClassName?: string;
  contentClassName?: string;
  overlayClassName?: string;
  closeOnOverlayClick?: boolean;
  // If true, allow Google Translate and avoid unmounting on close
  translateFriendly?: boolean;
}

// delay before the corner sparkles start, measured from the open animation start
// (the popup hits full size at ~400ms, so this stays comfortably before the peak)
const SPARKLE_DELAY_MS = 200;

const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  title = "",
  children,
  className = "",
  style,
  headerClassName = "",
  contentClassName = "",
  overlayClassName = "",
  closeOnOverlayClick = true,
  translateFriendly = false,
}: PopupProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [content, setContent] = useState<React.ReactNode>(null);
  const [showInitialStyles, setShowInitialStyles] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>(0);
  const prevIsOpenRef = useRef<boolean>(false);
  const sparkleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const { width } = useViewportSize();
  const isDesktop = width >= 940;
  const [showSparkles, setShowSparkles] = useState(false);
  const peakFiredRef = useRef(false);

  const elasticAnimation = useCallback(
    (currentTime: number, isOpening: boolean, onComplete?: () => void, onPeak?: () => void) => {
      if (!popupRef.current) return;

      const t = (currentTime - startTimeRef.current) / 1000;

      const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);
      const easeInOutCubic = (x: number) =>
        x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

      if (isOpening) {
        let scale = 0.5;
        let opacity = 0;

        if (t < 0.4) {
          const progress = easeOutCubic(t / 0.4);
          scale = 0.5 + (1.05 - 0.5) * progress;
          opacity = easeOutCubic(t / 0.3);
        } else if (t < 0.7) {
          const progress = easeInOutCubic((t - 0.4) / 0.3);
          scale = 1.05 - (1.05 - 0.99) * progress;
          opacity = 1;
        } else if (t < 1.0) {
          const progress = easeOutCubic((t - 0.7) / 0.3);
          scale = 0.99 + (1 - 0.99) * progress;
          opacity = 1;
        } else {
          scale = 1;
          opacity = 1;
        }

        popupRef.current.style.transform = `scale(${scale})`;
        popupRef.current.style.opacity = `${opacity}`;

        if (t >= 0.4 && !peakFiredRef.current) {
          peakFiredRef.current = true;
          if (onPeak) onPeak();
        }

        if (t < 1.0) {
          animationRef.current = requestAnimationFrame((time) =>
            elasticAnimation(time, isOpening, onComplete, onPeak)
          );
        } else {
          popupRef.current.style.transform = "scale(1)";
          popupRef.current.style.opacity = "1";
          setShowInitialStyles(false);
          if (onComplete) onComplete();
        }
      } else {
        const progress = easeInOutCubic(Math.min(t * 2.5, 1));
        const scale = 1 - (1 - 0.85) * progress;
        const opacity = 1 - progress;

        popupRef.current.style.transform = `scale(${scale})`;
        popupRef.current.style.opacity = `${opacity}`;

        if (t < 0.4) {
          animationRef.current = requestAnimationFrame((time) =>
            elasticAnimation(time, isOpening, onComplete, onPeak)
          );
        } else {
          if (onComplete) onComplete();
        }
      }
    },
    [popupRef]
  );

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    const isOpening = !wasOpen && isOpen;
    const isClosingNow = wasOpen && !isOpen;

    if (isOpening) {
      if (!isMounted) {
        setIsMounted(true);
      }
      setIsClosing(false);
      setContent(children);
      setShowInitialStyles(true);
      // start the corner sparkles a bit into the open animation (not right away, not too late)
      setShowSparkles(false);
      if (sparkleTimerRef.current) clearTimeout(sparkleTimerRef.current);
      sparkleTimerRef.current = setTimeout(() => setShowSparkles(true), SPARKLE_DELAY_MS);
      peakFiredRef.current = false;

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      // Start elastic animation (wait until popupRef exists)
      const waitForPopupRef = () => {
        if (!popupRef.current) {
          animationRef.current = requestAnimationFrame(waitForPopupRef);
          return;
        }

        // Set initial styles
        popupRef.current.style.transform = "scale(0.5)";
        popupRef.current.style.opacity = "0";
        popupRef.current.style.transition = "none";

        // Use a double RAF to ensure styles are applied before animation starts
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Start the animation with current time
            startTimeRef.current = performance.now();
            animationRef.current = requestAnimationFrame((animTime) =>
              elasticAnimation(animTime, true)
            );
          });
        });
      };

      animationRef.current = requestAnimationFrame(waitForPopupRef);
    } else if (isClosingNow && isMounted) {
      setIsClosing(true);
      setShowInitialStyles(false);
      if (sparkleTimerRef.current) clearTimeout(sparkleTimerRef.current);
      setShowSparkles(false);

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      // Start closing animation
      startTimeRef.current = performance.now();
      animationRef.current = requestAnimationFrame((time) =>
        elasticAnimation(time, false, () => {
          if (!translateFriendly) {
            setIsMounted(false);
            setContent(null);
          }
          setIsClosing(false);
        })
      );
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, isMounted, children, translateFriendly, elasticAnimation]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (sparkleTimerRef.current) {
        clearTimeout(sparkleTimerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen && !isClosing) {
      setContent(children);
    }
  }, [children, isOpen, isClosing]);

  const popupContent = (
    <div
      className={styles.popupContainer}
      aria-hidden={!isOpen}
      style={{
        display:
          isOpen || isClosing ? undefined : translateFriendly && isMounted ? "none" : undefined,
      }}
    >
      <div
        className={`${styles.overlay} ${overlayClassName} ${isOpen && !isClosing ? styles.visible : ""}`}
        onClick={handleOverlayClick}
      ></div>
      <div className={styles.popupWrap}>
        {isDesktop && showSparkles && <PopupCornerSparkles />}
        <div
          ref={popupRef}
          className={`${styles.popup} ${className}`}
          style={
            showInitialStyles
              ? {
                  ...style,
                  transform: "scale(0.5)",
                  opacity: "0",
                  transition: "none",
                }
              : style
          }
        >
          <div className={`${styles.header} ${headerClassName}`}>
            {typeof title === "string" ? (
              <p className={styles.headerP} translate="yes">
                {title}
              </p>
            ) : (
              title
            )}
            <XClose className={styles.close} onClick={handleClose} />
          </div>
          <div className={`${styles.content} ${contentClassName}`} translate="yes">
            {content}
          </div>
        </div>
      </div>
    </div>
  );

  const getOrCreatePopupRoot = (): HTMLElement => {
    let el = document.getElementById("popup-root");
    if (!el) {
      const rootDiv = document.createElement("div");
      rootDiv.id = "popup-root";
      // Prevent Google Translate unless translateFriendly
      if (!translateFriendly) {
        rootDiv.classList.add("notranslate");
        rootDiv.setAttribute("translate", "no");
      }
      document.body.appendChild(rootDiv);
      el = rootDiv;
    } else {
      // Toggle attributes based on translateFriendly
      if (!translateFriendly) {
        el.classList.add("notranslate");
        (el as HTMLElement).setAttribute("translate", "no");
      } else {
        el.classList.remove("notranslate");
        (el as HTMLElement).removeAttribute("translate");
      }
    }
    return el;
  };

  return isMounted ? createPortal(popupContent, getOrCreatePopupRoot()) : null;
};

const createPopupRoot = () => {
  if (!document.getElementById("popup-root")) {
    const rootDiv = document.createElement("div");
    rootDiv.id = "popup-root";
    document.body.appendChild(rootDiv);
  }
};

createPopupRoot();

export default Popup;
